import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";

// Material-UI Components
import { Box, useMediaQuery, useTheme } from "@mui/material";

const ThreeScene = () => {
  const containerRef = useRef(null);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    const container = containerRef.current;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(window.devicePixelRatio); // Improve rendering on high-DPI screens
    renderer.shadowMap.enabled = true; // Enable shadow map
    renderer.shadowMap.type = THREE.PCFSoftShadowMap; // Softer shadows
    container.appendChild(renderer.domElement);

    // Camera
    const fov = 80;
    const aspect = container.clientWidth / container.clientHeight;
    const near = 0.1;
    const far = 10;
    const camera = new THREE.PerspectiveCamera(fov, aspect, near, far);
    camera.position.set(1, 1, 2); // Adjusted position for better angle

    // Scene
    const scene = new THREE.Scene();
    const bgColor = "#023047"; // Dynamically set background color
    scene.background = new THREE.Color(bgColor);

    // OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.03;
    controls.enableZoom = false; // Disable zooming
    controls.enableRotate = true;

    // Geometry and Materials
    const geo = new THREE.IcosahedronGeometry(1.2, 4.0);
    const mat = new THREE.MeshBasicMaterial({
      color: isSmallScreen ? "#023047" : "#ffffff", // Swap colors based on screen size
      transparent: true,
      opacity: 1.0,
    });
    const wireMat = new THREE.MeshBasicMaterial({
      color: isSmallScreen ? "#ffffff" : "#023047", // Swap wireframe color
      wireframe: true,
    });
    const wireMesh = new THREE.Mesh(geo, wireMat);
    wireMesh.scale.setScalar(1.001);
    const mesh = new THREE.Mesh(geo, mat);
    mesh.add(wireMesh);
    mesh.castShadow = true; // Allow the mesh to cast shadows
    scene.add(mesh);

    // Ground Plane to receive shadows
    const planeGeo = new THREE.PlaneGeometry(10, 10);
    const planeMat = new THREE.ShadowMaterial({ opacity: 0.3 });
    const plane = new THREE.Mesh(planeGeo, planeMat);
    plane.rotation.x = -Math.PI / 2; // Rotate to horizontal
    plane.position.y = -1.5; // Position below the object
    plane.receiveShadow = true; // Allow the plane to receive shadows
    scene.add(plane);

    // Hemisphere Light (Subtle ambient light)
    const hemiLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 1);
    scene.add(hemiLight);

    // Point Light (Primary light source)
    const pointLight = new THREE.AmbientLight(0xffffff, 1, 10);
    pointLight.position.set(2, 2, 3);
    scene.add(pointLight);

    // Update light position based on cursor
    const handleMouseMove = (event) => {
      const rect = container.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

      // Update light position dynamically
      pointLight.position.set(x * 5, y * 5, 3);
    };

    container.addEventListener("mousemove", handleMouseMove);

    // Resize handler
    const handleResize = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      renderer.setSize(width, height);
      camera.fov = width < 600 ? 100 : 80; // Adjust FOV for smaller screens
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };

    window.addEventListener("resize", handleResize);
    handleResize(); // Initialize size

    // Animation loop
    const animate = () => {
      requestAnimationFrame(animate);

      // Rotate the model
      mesh.rotation.y += 0.003; // Rotate on Y-axis
      mesh.rotation.x -= 0.002; // Rotate on X-axis

      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // Cleanup on unmount
    return () => {
      if (container) {
        renderer.dispose();
        controls.dispose();
        window.removeEventListener("resize", handleResize);
        container.removeEventListener("mousemove", handleMouseMove);
        container.removeChild(renderer.domElement);
      }
    };
  }, [isSmallScreen]);

  return (
    <Box
      ref={containerRef}
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100vh",
        overflow: "hidden",
      }}
    />
  );
};

export default ThreeScene;
